import { memoize, uuid, getFileExtension } from "lib";
import gifFrames from "gif-frames";
import { VIDEO_MEDIA_TYPES } from "lib/constants";

// Cache for GIF frame data (all frames extracted once)
const gifFrameCache = new Map();

const IMAGE_SOURCE_RETRY_TIME = 500;
const MAX_RETRY = 20;

const getSourceImage = async (gifSource, resolve, reject, attempt = 1) => {
  if (attempt < MAX_RETRY) {
    try {
      const sourceImage = await fetch(`${gifSource}?${uuid()}`);
      if (!sourceImage.ok) {
        setTimeout(() => {
          getSourceImage(gifSource, resolve, reject, attempt + 1);
        }, IMAGE_SOURCE_RETRY_TIME);
      } else {
        return resolve(sourceImage);
      }
    } catch (error) {
      // fetch() throws on network/CORS errors — reject immediately instead of
      // leaving the outer promise hanging (the async function's implicit promise
      // rejection would go unhandled without this)
      return reject(error);
    }
  } else {
    return reject("");
  }
};

export const getTextMaskSource = ({
  color,
  source,
  filters,
  canvas,
  opacity
} = {}) =>
  new Promise(async resolve => {
    const ctx = canvas.getContext("2d");
    // remove any old content on the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const tempImg = document.createElement("img");
    document.body.appendChild(tempImg);
    tempImg.onload = () => {
      const tempImgRect = tempImg.getBoundingClientRect();
      const width = tempImgRect.width;
      const height = tempImgRect.height;

      // set the canvas to the full res image dimensions
      ctx.canvas.setAttribute("width", width);
      ctx.canvas.setAttribute("height", height);

      document.body.appendChild(canvas);

      // draw the background for the image
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.rect(0, 0, width, height);
      ctx.stroke();
      ctx.fill();
      // apply filters only to the image
      if (filters && filters.length > 0) {
        ctx.filter = filters;
      }
      // apply opacity only to the image
      if (opacity) {
        ctx.globalAlpha = opacity;
      }
      // add the image as an overlay on the block color background
      ctx.drawImage(tempImg, 0, 0);

      // return our new data url and remove the temporary elements from the document
      resolve(canvas.toDataURL());
      document.body.removeChild(tempImg);
      document.body.removeChild(canvas);
    };
    tempImg.src = source;
  });

export const getSourceAsLocalUrl = async gifSource => {
  if (gifSource.startsWith("blob:")) {
    // when the url is already a local one we don't need to process it any more
    return gifSource;
  } else {
    let image;
    try {
      image = await new Promise((resolve, reject) =>
        getSourceImage(gifSource, resolve, reject)
      );
    } catch (error) {
      console.error(
        new Error(
          `source '${gifSource}' failed to be loaded after ${MAX_RETRY} attempts`
        )
      );
      return null;
    }

    const imageBlob = await image.blob();
    return window.URL.createObjectURL(imageBlob);
  }
};

export const getGifImageSource = async ({ isPlaying, gifSource }) => {
  if (!isPlaying) {
    const sourceAsLocalUrl = await getSourceAsLocalUrl(gifSource);

    if (!sourceAsLocalUrl) {
      return "";
    }

    const frameData = await gifFrames({
      url: sourceAsLocalUrl,
      frames: 0,
      outputType: "canvas"
    });
    return frameData[0].getImage().toDataURL();
  }
  return gifSource;
};

export const memoizedGetGifImageSource = memoize(
  getGifImageSource,
  ({ isPlaying, gifSource, isLooped }) =>
    `${Boolean(isPlaying)}-${Boolean(isLooped)}-${gifSource}`
);

export const isMediaVideoFormat = mediaSource => {
  if (!mediaSource || !(typeof mediaSource === "string")) {
    return false;
  }
  const sourceSuffix = mediaSource.split(".").pop();
  if (!sourceSuffix) {
    return false;
  }

  return VIDEO_MEDIA_TYPES.includes(sourceSuffix.toLowerCase());
};

export const getMediaType = media => {
  const fileExtension = getFileExtension(media.name);

  if (fileExtension === "gif") {
    return "ANIMATION";
  } else if (fileExtension === "mp4") {
    return "VIDEO";
  } else {
    return "IMAGE";
  }
};

/**
 * Extracts all frames from a GIF and caches them.
 * Returns an array of frame data with timing info.
 *
 * @param {string} gifSource - The URL of the GIF
 * @returns {Promise<{frames: Array, totalDuration: number}>}
 */
export const extractGifFrames = async gifSource => {
  if (gifFrameCache.has(gifSource)) {
    return gifFrameCache.get(gifSource);
  }

  const sourceAsLocalUrl = await getSourceAsLocalUrl(gifSource);
  if (!sourceAsLocalUrl) {
    return { frames: [], totalDuration: 0 };
  }

  const frameData = await gifFrames({
    url: sourceAsLocalUrl,
    frames: "all",
    outputType: "canvas",
    cumulative: false // Don't accumulate - extract each frame independently
  });

  // Extract frame info with timing (GIF delay is in centiseconds)
  // Clone each canvas immediately and convert to data URL to preserve frame state
  const frames = frameData.map(frame => {
    const sourceCanvas = frame.getImage();
    // Clone the canvas to preserve this frame's state
    const clonedCanvas = document.createElement("canvas");
    clonedCanvas.width = sourceCanvas.width;
    clonedCanvas.height = sourceCanvas.height;
    const ctx = clonedCanvas.getContext("2d");
    ctx.drawImage(sourceCanvas, 0, 0);
    // Convert to data URL immediately to freeze the frame
    const dataUrl = clonedCanvas.toDataURL();

    return {
      dataUrl,
      delay: (frame.frameInfo.delay || 10) * 10 // Convert to milliseconds
    };
  });

  // Calculate total duration
  let totalDuration = 0;
  frames.forEach(f => {
    totalDuration += f.delay;
  });

  const result = { frames, totalDuration };
  gifFrameCache.set(gifSource, result);
  return result;
};

/**
 * Gets a specific frame from a GIF based on timeline position.
 * Used for scrubbing GIF text masks.
 *
 * @param {string} gifSource - The URL of the GIF
 * @param {number} timeMs - The time position in milliseconds
 * @param {boolean} loop - Whether the GIF should loop
 * @returns {Promise<string>} - Data URL of the frame
 */
export const getGifFrameAtTime = async (gifSource, timeMs, loop = true) => {
  const { frames, totalDuration } = await extractGifFrames(gifSource);

  if (frames.length === 0) {
    return "";
  }

  // Handle looping
  let targetTime = timeMs;
  if (loop && totalDuration > 0) {
    targetTime = timeMs % totalDuration;
  } else if (timeMs >= totalDuration) {
    // Return last frame if not looping and past duration
    return frames[frames.length - 1].dataUrl;
  }

  // Find the frame at the target time
  let accumulatedTime = 0;
  for (let i = 0; i < frames.length; i++) {
    accumulatedTime += frames[i].delay;
    if (targetTime < accumulatedTime) {
      return frames[i].dataUrl;
    }
  }

  // Fallback to last frame
  return frames[frames.length - 1].dataUrl;
};

/**
 * Preloads GIF frames for faster scrubbing.
 * Call this when a GIF element is first rendered.
 *
 * @param {string} gifSource - The URL of the GIF to preload
 */
export const preloadGifFrames = gifSource => {
  extractGifFrames(gifSource).catch(err => {
    console.warn("Failed to preload GIF frames:", err);
  });
};
